import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { loginComplete } from '../lib/api';

/**
 * /login/complete?link_id=<id>&t=<nonce>
 *
 * Landing page for the magic link in the user's email. We POST the link_id
 * and nonce to /oauth/login/complete, which:
 *   1. Asks Cognito to verify the nonce (VerifyAuthChallenge fires).
 *   2. Mints an opaque auth_code tied to the original OAuth session.
 *   3. Returns a `redirect` URL pointing back at whichever client started
 *      this flow — could be ChatGPT/Claude (their custom redirect_uri), or
 *      our own site (/auth/callback).
 *
 * We immediately follow that redirect — there's nothing for the user to
 * see here except a brief loading state.
 */
export default function LoginComplete() {
  const [params] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const linkId = params.get('link_id') ?? '';
    const nonce = params.get('t') ?? '';

    if (!linkId || !nonce) {
      setError('This sign-in link is missing required parameters.');
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { redirect } = await loginComplete(linkId, nonce);
        if (cancelled) return;
        if (!redirect) {
          setError('Sign-in succeeded but the server returned no redirect URL.');
          return;
        }
        window.location.assign(redirect);
      } catch (err) {
        if (cancelled) return;
        setError((err as Error).message);
      }
    })();

    return () => { cancelled = true; };
  }, [params]);

  if (error) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center">
        <div className="text-5xl mb-6">⚠️</div>
        <h1 className="text-2xl md:text-3xl font-bold">Sign-in link failed</h1>
        <p className="mt-6 text-slate-300">{error}</p>
        <p className="mt-6 text-sm text-slate-400">
          Magic links expire after 15 minutes and can only be used once. If yours expired,
          start over from <a className="text-amber-400 hover:text-amber-300" href="/manage">Manage</a>{' '}
          or <a className="text-amber-400 hover:text-amber-300" href="/setup">Setup</a>.
        </p>
        <p className="mt-4 text-sm text-slate-500">
          Still stuck? Email{' '}
          <a className="text-amber-400 hover:text-amber-300" href="mailto:support@thedraftlion.com">
            support@thedraftlion.com
          </a>.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center">
      <div className="text-5xl mb-6">🦁</div>
      <h1 className="text-2xl md:text-3xl font-bold">Signing you in…</h1>
      <p className="mt-6 text-slate-300">Hang tight — this only takes a moment.</p>
    </div>
  );
}
